import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

type ImportError = { row: number; column?: string; value?: string; message: string };

type Preview = {
  kind: string;
  source?: string; // 's3' quand l'aperçu vient du fichier S3 (sinon upload de fichier)
  filename: string;
  encoding: string;
  delimiter: string;
  totalRows: number;
  rowsOk: number;
  rowsError: number;
  errors: ImportError[];
  warnings: { row?: number; message: string }[];
  mapping: { matched: Record<string, string>; ignored: string[] };
  sample: Record<string, unknown>[];
  lineCounts?: Record<string, number>;
  replaceImpact?: { deactivate: number; byLine: Record<string, number> };
  canApply: boolean;
};

type Batch = {
  id: number;
  kind: string;
  filename: string;
  rowsOk: number;
  rowsError: number;
  createdAt: string;
  by: string;
};

/** multipart : on ne passe pas par api(), qui impose du JSON. */
async function postFile<T>(path: string, kind: string, mode: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('mode', mode);
  form.append('file', file);
  const res = await fetch(path, { method: 'POST', body: form, credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? `Erreur ${res.status}`);
  return body as T;
}

export default function Imports() {
  const [kind, setKind] = useState<'CUSTOMERS' | 'PRODUCTS'>('PRODUCTS');
  const [mode, setMode] = useState<'MERGE' | 'REPLACE'>('MERGE');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const history = useQuery<{ batches: Batch[] }>({
    queryKey: ['imports'],
    queryFn: async () => (await fetch('/api/admin/imports', { credentials: 'include' })).json(),
  });

  const analyse = useMutation({
    mutationFn: (f: File) => postFile<Preview>('/api/admin/imports/preview', kind, mode, f),
    onSuccess: (data) => {
      setPreview(data);
      setDone(null);
    },
  });

  const commit = useMutation({
    mutationFn: (f: File) =>
      postFile<{ rowsApplied: number; deactivated?: number }>(
        '/api/admin/imports/commit',
        kind,
        mode,
        f,
      ),
    onSuccess: (data) => {
      const retire = data.deactivated ? `, ${data.deactivated} produit(s) retiré(s)` : '';
      setDone(`${data.rowsApplied} ligne(s) importée(s)${retire}.`);
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      void qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  // Import depuis S3 : pas de fichier local, la source est le fichier configuré
  // côté serveur (S3_CUSTOMERS_KEY). Réutilise l'aperçu et la validation habituels.
  async function postJson<T>(path: string): Promise<T> {
    const res = await fetch(path, { method: 'POST', credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, body.error ?? `Erreur ${res.status}`);
    return body as T;
  }

  const analyseS3 = useMutation({
    mutationFn: () => postJson<Preview>('/api/admin/imports/s3/preview'),
    onSuccess: (data) => {
      setKind('CUSTOMERS');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setPreview(data);
      setDone(null);
    },
  });

  const commitS3 = useMutation({
    mutationFn: () => postJson<{ rowsApplied: number }>('/api/admin/imports/s3/commit'),
    onSuccess: (data) => {
      setDone(`${data.rowsApplied} client(s) importé(s) depuis S3.`);
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  function choose(f: File | null) {
    setFile(f);
    setPreview(null);
    setDone(null);
    if (f) analyse.mutate(f);
  }

  // L'aperçu du mode « Remplacer » dépend du mode : on ré-analyse quand il change
  // (l'impact « X produits retirés » doit refléter le mode courant).
  useEffect(() => {
    if (file) analyse.mutate(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className="space-y-8">
      <section className="bg-card space-y-4 rounded-xl border p-6">
        <h2 className="text-lg font-semibold">Importer un fichier</h2>

        <div className="flex flex-wrap items-end gap-4">
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Type</span>
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as 'CUSTOMERS' | 'PRODUCTS');
                setMode('MERGE');
                choose(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="border-input bg-background h-11 rounded-lg border px-3"
            >
              <option value="PRODUCTS">Produits</option>
              <option value="CUSTOMERS">Clients</option>
            </select>
          </label>

          {kind === 'PRODUCTS' && (
            <fieldset className="space-y-1.5">
              <span className="block text-sm font-medium">Mode</span>
              <div className="border-input flex overflow-hidden rounded-lg border">
                <button
                  type="button"
                  onClick={() => {
                    setMode('MERGE');
                    setDone(null);
                  }}
                  aria-pressed={mode === 'MERGE'}
                  className={`h-11 px-3 text-sm font-medium ${mode === 'MERGE' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground'}`}
                >
                  Ajouter / mettre à jour
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('REPLACE');
                    setDone(null);
                  }}
                  aria-pressed={mode === 'REPLACE'}
                  className={`border-input h-11 border-l px-3 text-sm font-medium ${mode === 'REPLACE' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground'}`}
                >
                  Remplacer la ligne
                </button>
              </div>
            </fieldset>
          )}

          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Fichier CSV</span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => choose(e.target.files?.[0] ?? null)}
              className="border-input h-11 rounded-lg border px-3 py-2 text-sm"
            />
          </label>

          <a
            href={`/api/admin/imports/templates/${kind.toLowerCase()}`}
            className="text-primary h-11 self-end text-sm underline underline-offset-4"
          >
            Télécharger un modèle
          </a>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <button
            type="button"
            onClick={() => analyseS3.mutate()}
            disabled={analyseS3.isPending}
            className="border-input h-11 rounded-lg border px-4 text-sm font-medium disabled:opacity-40"
          >
            {analyseS3.isPending ? 'Lecture depuis S3…' : 'Importer les clients depuis S3'}
          </button>
          <span className="text-muted-foreground text-xs">
            Récupère la dernière version du fichier client sur S3 (source unique) et l'affiche en
            aperçu avant application.
          </span>
        </div>
        {analyseS3.isError && (
          <p className="text-destructive text-sm font-medium">{(analyseS3.error as Error).message}</p>
        )}

        {analyse.isPending && <p className="text-muted-foreground text-sm">Analyse du fichier…</p>}
        {analyse.isError && (
          <p className="text-destructive text-sm font-medium">{(analyse.error as Error).message}</p>
        )}
        {done && <p className="text-sm font-medium text-green-700 dark:text-green-400">{done}</p>}
      </section>

      {preview && (
        <section className="bg-card space-y-4 rounded-xl border p-6">
          <h2 className="text-lg font-semibold">Aperçu — {preview.filename}</h2>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-muted-foreground">Lignes lues</dt>
            <dd className="font-medium">{preview.totalRows}</dd>
            <dt className="text-muted-foreground">Valides</dt>
            <dd className="font-medium">{preview.rowsOk}</dd>
            <dt className="text-muted-foreground">En erreur</dt>
            <dd className={preview.rowsError ? 'text-destructive font-medium' : 'font-medium'}>
              {preview.rowsError}
            </dd>
            <dt className="text-muted-foreground">Encodage détecté</dt>
            <dd className="font-medium">{preview.encoding}</dd>
          </dl>

          <details className="rounded-lg border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Correspondance des colonnes ({Object.keys(preview.mapping.matched).length} reconnue(s))
            </summary>
            <div className="mt-3 space-y-2 text-sm">
              <ul className="grid gap-1 sm:grid-cols-2">
                {Object.entries(preview.mapping.matched).map(([canonical, source]) => (
                  <li key={canonical} className="font-mono text-xs">
                    <span className="text-muted-foreground">{source}</span> → {canonical}
                  </li>
                ))}
              </ul>
              {preview.mapping.ignored.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  Ignorées : {preview.mapping.ignored.join(', ')}
                </p>
              )}
            </div>
          </details>

          {preview.warnings.length > 0 && (
            <ul className="space-y-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              {preview.warnings.map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </ul>
          )}

          {preview.lineCounts && Object.keys(preview.lineCounts).length > 0 && (
            <p className="text-sm">
              Par ligne :{' '}
              {Object.entries(preview.lineCounts)
                .map(([code, n]) => `${code} (${n})`)
                .join(', ')}
            </p>
          )}

          {preview.replaceImpact && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium">Mode « Remplacer »</p>
              {preview.replaceImpact.deactivate === 0 ? (
                <p className="text-muted-foreground">
                  Aucun produit à retirer — le fichier couvre déjà tout ce qui existe dans ces
                  lignes.
                </p>
              ) : (
                <p>
                  {preview.replaceImpact.deactivate} produit(s) présent(s) en base mais absent(s) du
                  fichier seront <b>retirés du catalogue</b> (désactivés)
                  {Object.keys(preview.replaceImpact.byLine).length > 0 && (
                    <>
                      {' — '}
                      {Object.entries(preview.replaceImpact.byLine)
                        .map(([code, n]) => `${code} (${n})`)
                        .join(', ')}
                    </>
                  )}
                  . L'historique des commandes reste intact.
                </p>
              )}
            </div>
          )}

          {preview.rowsError > 0 && (
            <div className="space-y-2">
              <h3 className="text-destructive font-medium">
                Rien ne sera importé tant que ces erreurs subsistent
              </h3>
              <div className="max-h-80 overflow-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Ligne</th>
                      <th className="p-2 text-left font-medium">Colonne</th>
                      <th className="p-2 text-left font-medium">Valeur</th>
                      <th className="p-2 text-left font-medium">Problème</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono">{e.row}</td>
                        <td className="p-2 font-mono">{e.column ?? '—'}</td>
                        <td className="p-2 font-mono">{e.value ?? '—'}</td>
                        <td className="p-2">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(commit.isError || commitS3.isError) && (
            <p className="text-destructive text-sm font-medium">
              {((commit.error || commitS3.error) as Error).message}
            </p>
          )}

          <button
            disabled={
              !preview.canApply ||
              commit.isPending ||
              commitS3.isPending ||
              (preview.source !== 's3' && !file)
            }
            onClick={() => {
              if (preview.source === 's3') {
                commitS3.mutate();
                return;
              }
              if (!file) return;
              if (mode === 'REPLACE') {
                const n = preview.replaceImpact?.deactivate ?? 0;
                if (
                  n > 0 &&
                  !confirm(
                    `Mode Remplacer : ${n} produit(s) absent(s) du fichier seront retirés du catalogue (désactivés). Continuer ?`,
                  )
                ) {
                  return;
                }
              }
              commit.mutate(file);
            }}
            className="bg-primary text-primary-foreground h-12 rounded-lg px-6 font-semibold disabled:opacity-40"
          >
            {commit.isPending || commitS3.isPending
              ? 'Import en cours…'
              : preview.source === 's3'
                ? `Importer ${preview.rowsOk} client(s) depuis S3`
                : mode === 'REPLACE'
                  ? `Remplacer — ${preview.rowsOk} ligne(s)`
                  : `Importer ${preview.rowsOk} ligne(s)`}
          </button>
        </section>
      )}

      <section className="bg-card space-y-4 rounded-xl border p-6">
        <div>
          <h2 className="text-lg font-semibold">Configuration (Lignes, Cadeaux, Rôles)</h2>
          <p className="text-muted-foreground text-sm">
            Réimporte un fichier exporté depuis <b>Export → Configuration</b>. <b>Remplace tout</b> le
            contenu de la page : ce qui est absent du fichier est retiré (les lignes/reps encore
            utilisés sont désactivés plutôt que supprimés). Une <b>sauvegarde de sécurité</b> est
            prise avant toute écriture. Pour les <b>Rôles</b>, laissez la colonne « mot_de_passe »
            vide : un mot de passe est généré pour chaque nouveau représentant et affiché ici (à
            noter, non récupérable).
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <ConfigImportRow
            label="Lignes"
            importPath="/api/admin/config/lines"
            onImported={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'lines'] });
              void qc.invalidateQueries({ queryKey: ['admin', 'rewards'] });
            }}
          />
          <ConfigImportRow
            label="Cadeaux"
            importPath="/api/admin/config/gifts"
            onImported={() => void qc.invalidateQueries({ queryKey: ['admin', 'rewards'] })}
          />
          <ConfigImportRow
            label="Rôles"
            importPath="/api/admin/config/reps"
            onImported={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'reps'] });
              void qc.invalidateQueries({ queryKey: ['admin', 'rewards'] });
              void qc.invalidateQueries({ queryKey: ['admin', 'lines'] });
            }}
          />
        </div>
      </section>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-3 text-lg font-semibold">Historique</h2>
        {history.data?.batches.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-left">
                <th className="pb-2 font-medium">Date</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Fichier</th>
                <th className="pb-2 font-medium">OK</th>
                <th className="pb-2 font-medium">Erreurs</th>
                <th className="pb-2 font-medium">Par</th>
              </tr>
            </thead>
            <tbody>
              {history.data.batches.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="py-2">{new Date(b.createdAt).toLocaleString('fr-CA')}</td>
                  <td className="py-2">{b.kind === 'CUSTOMERS' ? 'Clients' : 'Produits'}</td>
                  <td className="py-2 font-mono text-xs">{b.filename}</td>
                  <td className="py-2">{b.rowsOk}</td>
                  <td className={b.rowsError ? 'text-destructive py-2' : 'py-2'}>{b.rowsError}</td>
                  <td className="py-2">{b.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted-foreground text-sm">Aucun import pour l'instant.</p>
        )}
      </section>
    </div>
  );
}

type ConfigImportResult = {
  message: string;
  warnings: string[];
  passwords?: { username: string; password: string }[];
};

/**
 * Import « remplacer-tout » d'une page de configuration (Lignes, Cadeaux, Rôles).
 * POST direct du fichier ; le serveur prend une sauvegarde de sécurité avant écriture.
 */
function ConfigImportRow({
  label,
  importPath,
  onImported,
}: {
  label: string;
  importPath: string;
  onImported: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConfigImportResult | null>(null);
  const [error, setError] = useState('');

  async function doImport(f: File) {
    if (
      !window.confirm(
        `Importer « ${label} » va REMPLACER tout le contenu de cette page. Une sauvegarde de sécurité est prise avant. Continuer ?`,
      )
    )
      return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(importPath, { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) {
        let msg = `Erreur ${res.status}`;
        try {
          const b = await res.json();
          if (b?.error) msg = b.error;
        } catch {
          /* réponse non-JSON */
        }
        throw new Error(msg);
      }
      setResult((await res.json()) as ConfigImportResult);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-input space-y-2 rounded-lg border p-4">
      <div className="font-semibold">{label}</div>
      <input
        ref={ref}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void doImport(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="border-input w-full rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-40"
      >
        {busy ? 'Import…' : '⬆ Importer (Excel)'}
      </button>

      {result && (
        <div className="rounded-lg border border-green-600/30 bg-green-600/5 p-3 text-sm">
          <p className="font-medium text-green-700 dark:text-green-400">{result.message}</p>
          {result.warnings.length > 0 && (
            <ul className="text-muted-foreground mt-1 list-disc pl-5 text-xs">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          {result.passwords && result.passwords.length > 0 && (
            <div className="mt-2">
              <p className="font-medium">Mots de passe générés (à noter, non récupérables) :</p>
              <ul className="mt-1 font-mono text-xs">
                {result.passwords.map((p) => (
                  <li key={p.username}>
                    {p.username} : {p.password}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-destructive text-sm font-medium">{error}</p>}
    </div>
  );
}
